/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {SiteApiService, SiteModel} from './site-api.service';
import {SiteCopyModalComponent} from './site-copy-modal.component';
import {SiteItemPageComponent} from './site-item-page.component';
import {SiteListPageComponent} from './site-list-page.component';

export function siteStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(SiteApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisher.apiKey);
  const rh = adnRouterHelper.createRouterHelper('sites.site', SiteApiService, 'app.inv', 'app');
  const copyConfig = {modalComponent: SiteCopyModalComponent, stateUrl: 'app.inv.sites.site', lcase: 'site', ucase: 'Site'};

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: SiteListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      sitePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: SiteModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          if ($stateParams.earningsAccountId) {
            model.earningsAccount = {id: $stateParams.earningsAccountId, name: $stateParams.earningsAccountName};
            model.country = $stateParams.earningsAccountCountry;
            if ($stateParams.earningsAccountOwnerTeamId) {
              model.ownerTeam = {id: $stateParams.earningsAccountOwnerTeamId};
            }
          }
          return new SignalAdminResourcePageState(
            model, true, readOnly(), api, 'Site', 'name', copyConfig, undefined,
            {objectTypeKey: 'Site', crumbParams: ['EarningsAccount']}
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Site', 'name', copyConfig, undefined,
          {objectTypeKey: 'Site', crumbParams: ['EarningsAccount']}
        ));
      },
      model: (sitePage: SignalAdminResourcePageState<SiteModel>) => sitePage.model()
    },
    views: {
      'content@app': {component: SiteItemPageComponent, bindings: {state: 'sitePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'sitePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'sitePage'}}
    }
  }));
  return states.build();
}
